/**
 * DepartmentsAtGlance — Home dashboard row.
 *
 * One mini-card per department (top-N by combined heat score) showing
 * the highest-signal mini-KPIs + Department Head identity.
 *
 * Owner: drafts/55 v0.9.1.
 */

import { Link } from '@tanstack/react-router';
import { useDepartmentKpis, type DepartmentKpis } from '../../api/orgHooks';
import { useSiteScope } from '../../auth/SiteScopeContext';
import styles from './HomeDashboardCards.module.css';

function bandForCapa(open: number, overdue: number): 'green' | 'amber' | 'red' {
  if (overdue >= 2) return 'red';
  if (overdue >= 1 || open >= 4) return 'amber';
  return 'green';
}
function bandForTraining(pct: number): 'green' | 'amber' | 'red' {
  if (pct >= 8) return 'red';
  if (pct >= 3) return 'amber';
  return 'green';
}
function bandForDev(n: number): 'green' | 'amber' | 'red' {
  if (n >= 3) return 'red';
  if (n >= 1) return 'amber';
  return 'green';
}

const LIMIT = 6;

export function DepartmentsAtGlance() {
  const q = useDepartmentKpis();
  const { isAll, siteCode } = useSiteScope();
  if (!q.isLoading && (q.data?.items?.length ?? 0) === 0) return null;

  const items = (q.data?.items ?? [])
    .slice()
    .sort((a, b) => b.kpis.heat_score - a.kpis.heat_score)
    .slice(0, LIMIT);

  return (
    <section className={styles.deptSection} aria-label="Departments at a glance">
      <header className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>
          Departments at a glance
          {!isAll && <span className={styles.scopeChip}>{siteCode}</span>}
        </h2>
        <Link to="/admin/departments" className={styles.viewAll}>
          View org chart →
        </Link>
      </header>

      <div className={styles.deptGrid}>
        {q.isLoading && items.length === 0 && (
          <>
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
          </>
        )}
        {items.map((d) => (
          <DeptCard key={d.department_id} d={d} />
        ))}
      </div>
    </section>
  );
}

function DeptCard({ d }: { d: DepartmentKpis }) {
  return (
    <div className={styles.deptCard}>
      <div className={styles.deptCardHead}>
        <div className={styles.deptIdentity}>
          <span className={styles.deptCode}>{d.code}</span>
          {d.site_code && <span className={styles.deptSiteChip}>{d.site_code}</span>}
        </div>
        <h3 className={styles.deptName}>{d.name}</h3>
        {d.head && (
          <div className={styles.deptHead}>
            <span className={styles.deptHeadAvatar}>{d.head.initials}</span>
            <span className={styles.deptHeadMeta}>
              <span className={styles.deptHeadName}>{d.head.name.split(' (')[0]}</span>
              <span className={styles.deptHeadTitle}>{d.head.title}</span>
            </span>
          </div>
        )}
      </div>

      <div className={styles.deptKpiRow}>
        <DeptKpi
          label="CAPA"
          value={d.kpis.capa_open}
          band={bandForCapa(d.kpis.capa_open, d.kpis.capa_overdue)}
          sub={d.kpis.capa_overdue > 0 ? `${d.kpis.capa_overdue} late` : 'open'}
        />
        <DeptKpi
          label="Dev. CRT/MAJ"
          value={d.kpis.deviation_critical_major}
          band={bandForDev(d.kpis.deviation_critical_major)}
          sub="active"
        />
        <DeptKpi
          label="Training"
          value={`${d.kpis.training_overdue_pct}%`}
          band={bandForTraining(d.kpis.training_overdue_pct)}
          sub={`${d.kpis.training_overdue} overdue`}
        />
        <DeptKpi
          label="Docs"
          value={d.kpis.documents_active}
          band="green"
          sub="effective"
        />
      </div>
    </div>
  );
}

function DeptKpi({
  label,
  value,
  band,
  sub,
}: {
  label: string;
  value: number | string;
  band: 'green' | 'amber' | 'red';
  sub?: string;
}) {
  return (
    <div className={`${styles.deptKpi} ${styles[`band_${band}`]}`}>
      <span className={styles.deptKpiLabel}>{label}</span>
      <span className={styles.deptKpiValue}>{value}</span>
      {sub && <span className={styles.deptKpiSub}>{sub}</span>}
    </div>
  );
}
